import React, { useState } from "react";

import { Loader2, Plus, Minus } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useAuth } from "../../../contexts/AuthContext";
import { billingApi } from "../../../lib/api/billing";
import toast from "react-hot-toast";

interface AddFundsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Preset amounts
const PRESET_AMOUNTS = [10, 25, 50, 100];

export const AddFundsModal: React.FC<AddFundsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { getIdToken } = useAuth();
  const [amount, setAmount] = useState<number>(25);
  const [loading, setLoading] = useState(false);

  const handlePresetClick = (presetAmount: number) => {
    setAmount(presetAmount);
  };

  const handleIncrement = () => {
    setAmount((prev) => prev + 5);
  };

  const handleDecrement = () => {
    setAmount((prev) => Math.max(prev - 5, 5));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
    if (rawValue === '') {
      setAmount(0);
      return;
    }
    const value = parseInt(rawValue, 10);
    if (!isNaN(value)) {
      setAmount(value);
    }
  };

  const handleSubmit = async () => {
    if (amount < 5) {
      toast.error("Minimum amount is $5");
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const { url } = await billingApi.addCloudFunds(token, amount);
      window.location.href = url;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      console.error("Error creating add funds session:", err);
      toast.error(axiosErr.response?.data?.message || "Failed to start checkout");
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Funds to Cloud Balance</DialogTitle>
          <DialogDescription>
            Add funds to your cloud balance for usage-based services.
            These funds never expire and carry over between billing cycles.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Preset amounts */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Quick select</Label>
            <div className="flex gap-2">
              {PRESET_AMOUNTS.map((preset) => (
                <Button
                  key={preset}
                  variant={amount === preset ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePresetClick(preset)}
                  className="flex-1"
                >
                  ${preset}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom amount input */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-sm text-muted-foreground">
              Or enter custom amount
            </Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleDecrement}
                disabled={amount <= 5}
                className="h-10 w-10"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="amount"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={amount === 0 ? '' : amount}
                  onChange={handleInputChange}
                  className="pl-7 text-center text-lg font-medium"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleIncrement}
                className="h-10 w-10"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Minimum $5
            </p>
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount to add</span>
              <span className="font-medium">${amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-border/50">
              <span className="font-medium">Total charge</span>
              <span className="font-semibold">${amount.toFixed(2)} USD</span>
            </div>
            <p className="text-xs text-muted-foreground text-right">+ applicable tax</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || amount < 5}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
